import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import UIkit from "uikit";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const TypePicker = ({ value, onChange, options }) => (
  <div className="checklist-item-type-btn-group">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={`checklist-item-type-btn ${value === opt.value ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          onChange(opt.value);
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export const ITEM_TYPES = [
  { value: "yes_no", label: "Yes / No", intro: "A picker for Yes/ No responses. Eg: 'Is the equipment operational?'" },
  { value: "count", label: "Count", intro: "An input field for accepting counts. Eg: 'How many screws are present?'" },
  { value: "rating", label: "Rating", intro: "A rating scale for evaluating items. Eg: 'Rate the cleanliness of the property.'" },
  { value: "condition", label: "Condition", intro: "A picker for selecting item conditions from a predefined set of options: Good, Dirty, Damaged, Not Working, Missing, Worn, Unsafe. Eg: 'Select the condition of the equipment.'" },
  { value: "list", label: "List", intro: "A list of sub-tasks to complete. Eg: 'Clean the room: make the bed, dust the blinds, vacuum the floor'" },
];

const newItemForType = (type) => {
  const base = {
    item_type: type,
    label: "",
    description: "",
    is_required: false,
    require_photo: false,
    require_note: false,
  };

  if (type === "list") return { ...base, config: { items: [""] } };
  if (type === "count") return { ...base, config: { min: 0, max: 10 } };
  if (type === "rating") return { ...base, config: { scale_min: 1, scale_max: 5 } };
  if (type === "condition") return { ...base, config: null };

  return { ...base, config: null };
};

function useUikitModal(options = {}) {
  const ref = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    instanceRef.current = UIkit.modal(ref.current, {
      stack: true,
      container: false,
      escClose: true,
      bgClose: true,
      focus: true,
      ...options,
    });

    return () => {
      try {
        instanceRef.current?.$destroy?.(true);
      } catch (e) {}
      instanceRef.current = null;
    };
  }, []);

  const show = useCallback(() => instanceRef.current?.show(), []);
  const hide = useCallback(() => instanceRef.current?.hide(), []);

  return { ref, show, hide };
}

const ChecklistItemModal = ({
  open,
  mode = "add",
  initialValue = null,
  onClose,
  onSave,
  titleAdd = "Add Checklist Item",
  titleEdit = "Edit Checklist Item",
}) => {
  const { ref, show, hide } = useUikitModal();

  const [data, setData] = useState(() => {
    if (initialValue) {
      return structuredClone(initialValue);
    }
    return newItemForType("list");
  });

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialValue) {
      setData(structuredClone(initialValue));
    } else if (mode === "add" && initialValue) {
      const newItem = newItemForType(initialValue.item_type || "list");
      setData({
        ...newItem,
        _uiId: initialValue._uiId,
        id: initialValue.id,
      });
    } else {
      setData(newItemForType("list"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialValue?._uiId]);

  useEffect(() => {
    if (!open) {
      hide();
      return;
    }

    show();

    const t = setTimeout(() => {
      const el = ref.current;
      const input = el?.querySelector("input.uk-input, textarea.uk-textarea");
      input?.focus?.();
    }, 50);

    return () => clearTimeout(t);
  }, [open, show, hide]);

  // tell parent when modal is closed by UIkit (esc/backdrop/close)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handler = () => onClose?.();
    el.addEventListener("hidden", handler);

    return () => el.removeEventListener("hidden", handler);
  }, [onClose, ref]);

  const setType = (nextType) => {
    setData((prev) => {
      const next = newItemForType(nextType);
      return {
        ...next,
        id: prev.id,
        _uiId: prev._uiId,
        label: prev.label || "",
        description: prev.description || "",
        is_required: !!prev.is_required,
        require_photo: !!prev.require_photo,
        require_note: !!prev.require_note,
      };
    });
  };

  const handleListItemChange = (index, value) => {
    const items = [...(data.config?.items || [])];
    items[index] = value;
    setData((s) => ({
      ...s,
      config: { ...s.config, items },
    }));
  };

  const addListItem = () => {
    const items = [...(data.config?.items || []), ""];
    setData((s) => ({
      ...s,
      config: { ...s.config, items },
    }));
  };

  const removeListItem = (index) => {
    const items = [...(data.config?.items || [])];
    items.splice(index, 1);
    setData((s) => ({
      ...s,
      config: { ...s.config, items },
    }));
  };

  const updateConfigField = (field, value) => {
    setData((s) => ({
      ...s,
      config: { ...s.config, [field]: value },
    }));
  };

  const save = (e) => {
    e.preventDefault();

    const next = structuredClone(data);
    next.label = String(next.label || "").trim();
    next.description = String(next.description || "").trim();

    if (!next.label) {
      UIkit.notification({ message: "Label required.", status: "danger" });
      return;
    }

    switch (next.item_type) {
      case "list":
        if (!next.config?.items || next.config.items.length === 0) {
          UIkit.notification({ message: "At least one list option is required", status: "danger" });
          return;
        }
        next.config.items = next.config.items.map(item => String(item).trim()).filter(Boolean);
        if (next.config.items.length === 0) {
          UIkit.notification({ message: "All list options must have a value", status: "danger" });
          return;
        }
        break;
      case "count":
        const min = Number(next.config?.min);
        const max = Number(next.config?.max);
        if (isNaN(min) || isNaN(max)) {
          UIkit.notification({ message: "Min and max values are required", status: "danger" });
          return;
        }
        if (min >= max) {
          UIkit.notification({ message: "Min must be less than max", status: "danger" });
          return;
        }
        next.config = { min, max };
        break;
      case "rating":
        const scaleMin = Number(next.config?.scale_min);
        const scaleMax = Number(next.config?.scale_max);
        if (isNaN(scaleMin) || isNaN(scaleMax)) {
          UIkit.notification({ message: "Scale min and max values are required", status: "danger" });
          return;
        }
        if (scaleMin >= scaleMax) {
          UIkit.notification({ message: "Scale min must be less than scale max", status: "danger" });
          return;
        }
        next.config = { scale_min: scaleMin, scale_max: scaleMax };
        break;
    }

    onSave?.(next);
    hide();
  };

  const modalMarkup = (
    <div
      ref={ref}
      className="uk-modal"
      uk-modal="container: false; stack: true; esc-close: true; bg-close: true; focus: true"
    >
      <div
        className="uk-modal-dialog uk-modal-body task-modal-dialog"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="uk-margin-remove">{mode === "add" ? titleAdd : titleEdit}</h2>

        <form className="uk-form-stacked uk-margin-small-top" onSubmit={save}>
          <div className="uk-margin">
            <label className="uk-form-label">Type</label>
            <div className="uk-form-controls">
              <TypePicker value={data.item_type} onChange={setType} options={ITEM_TYPES} />
              {data.item_type && (
                <div className="uk-text-meta uk-margin-small-top">
                  {ITEM_TYPES.find(t => t.value === data.item_type)?.intro}
                </div>
              )}
            </div>
          </div>

          <div className="uk-margin">
            <label className="uk-form-label">Label*</label>
            <div className="uk-form-controls">
              <input
                className="uk-input"
                value={data.label ?? ""}
                onChange={(e) => setData((s) => ({ ...s, label: e.target.value }))}
                placeholder="e.g., Check kitchen cleanliness"
              />
            </div>
          </div>

          <div className="uk-margin">
            <label className="uk-form-label">Description</label>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                value={data.description ?? ""}
                onChange={(e) => setData((s) => ({ ...s, description: e.target.value }))}
                placeholder="Additional notes or instructions"
              />
            </div>
          </div>

          {data.item_type === "list" && (
            <div className="uk-margin">
              <label className="uk-form-label">List Items*</label>
              <div className="uk-form-controls">
                {(data.config?.items || []).map((item, idx) => (
                  <div key={idx} className="uk-flex uk-flex-middle uk-margin-small">
                    <input
                      className="uk-input uk-width-expand"
                      type="text"
                      value={item}
                      onChange={(e) => handleListItemChange(idx, e.target.value)}
                      placeholder={`List item ${idx + 1}`}
                    />
                    <button
                      type="button"
                      className="uk-button hms-simple-icon-btn"
                      aria-label="Delete list item"
                      onClick={() => removeListItem(idx)}
                      style={{ marginLeft: 8 }}
                    >
                      <img src="/images/delete-icon.svg" alt="Delete" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="uk-button uk-button-small hms-btn hms-gray-btn"
                  onClick={addListItem}
                >
                  + Add Option
                </button>
              </div>
            </div>
          )}

          {data.item_type === "count" && (
            <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
              <div>
                <label className="uk-form-label">Min</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    type="number"
                    value={data.config?.min ?? 0}
                    onChange={(e) => updateConfigField("min", e.target.value)}
                  />
                </div>
              </div>
              <div style={{ paddingLeft: '8px' }}>
                <label className="uk-form-label">Max</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    type="number"
                    value={data.config?.max ?? 10}
                    onChange={(e) => updateConfigField("max", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {data.item_type === "rating" && (
            <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
              <div>
                <label className="uk-form-label">Scale Min</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    type="number"
                    value={data.config?.scale_min ?? 1}
                    onChange={(e) => updateConfigField("scale_min", e.target.value)}
                  />
                </div>
              </div>
              <div style={{ paddingLeft: '8px' }}>
                <label className="uk-form-label">Scale Max</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    type="number"
                    value={data.config?.scale_max ?? 5}
                    onChange={(e) => updateConfigField("scale_max", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Required toggle */}
          <div className="uk-margin uk-flex uk-flex-middle uk-flex-between">
            <label className="uk-form-label uk-margin-remove">Required</label>
            <div className="autosend-checkbox">
              <label className="setting-switch">
                <input
                  type="checkbox"
                  checked={!!data.is_required}
                  onChange={(e) => setData((s) => ({ ...s, is_required: e.target.checked }))}
                />
                <span className="setting-slider setting-round"></span>
              </label>
            </div>
          </div>

          {/* TODO: Enable Photo Required toggle when backend support is added */}
          {/* <div className="uk-margin uk-flex uk-flex-middle uk-flex-between">
            <label className="uk-form-label uk-margin-remove">Photo Required</label>
            <div className="autosend-checkbox">
              <label className="setting-switch">
                <input
                  type="checkbox"
                  checked={!!data.require_photo}
                  onChange={(e) => setData((s) => ({ ...s, require_photo: e.target.checked }))}
                />
                <span className="setting-slider setting-round"></span>
              </label>
            </div>
          </div> */}

          <div className="uk-margin uk-flex uk-flex-middle uk-flex-between">
            <label className="uk-form-label uk-margin-remove">Note Required</label>
            <div className="autosend-checkbox">
              <label className="setting-switch">
                <input
                  type="checkbox"
                  checked={!!data.require_note}
                  onChange={(e) => setData((s) => ({ ...s, require_note: e.target.checked }))}
                />
                <span className="setting-slider setting-round"></span>
              </label>
            </div>
          </div>

          <div className="uk-flex uk-flex-left">
            <button type="submit" className="uk-button hms-btn hms-red-btn">
              Save
            </button>
            <button type="button" className="uk-button hms-btn uk-modal-close">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const portalTarget =
    document.querySelector("#work-order-add .uk-offcanvas-bar") ||
    document.querySelector("#work-order-add") ||
    document.querySelector("#checklist-modal .uk-offcanvas-bar") ||
    document.querySelector("#checklist-modal") ||
    document.body;

  return ReactDOM.createPortal(modalMarkup, portalTarget);
};

export default ChecklistItemModal;
